import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";

/**
 * Presentational settings-list primitives for the More hub. No domain logic, no
 * data fetching, no sensitive computation — a group wrapper + tappable rows.
 */
export function MoreSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  /** Optional context line under the section header (e.g. which
   *  organization an ADMIN section administers). */
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card/60 overflow-hidden" data-testid="more-section">
      <div className="px-4 pt-3 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground">
        {title}
      </div>
      {subtitle ? (
        <div className="px-4 pb-1.5 text-[11px] text-muted-foreground/80" data-testid="more-section-subtitle">
          {subtitle}
        </div>
      ) : null}
      <div className="divide-y divide-border">{children}</div>
    </section>
  );
}

export function MoreRow({
  icon: Icon,
  label,
  sublabel,
  right,
  onClick,
  testId,
}: {
  icon: typeof ChevronRight;
  label: string;
  sublabel?: string;
  /** Custom right slot; defaults to a chevron. */
  right?: ReactNode;
  onClick?: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex w-full items-center gap-3 px-4 py-3 text-start hover:bg-muted/50"
    >
      <span className="h-8 w-8 rounded-lg bg-sand/50 grid place-items-center shrink-0">
        <Icon className="h-4 w-4 text-coral" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium truncate">{label}</span>
        {sublabel ? (
          <span className="block text-xs text-muted-foreground truncate">{sublabel}</span>
        ) : null}
      </span>
      {right ?? <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
    </button>
  );
}
